import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { protocolService } from '../services/protocolService'
import { Protocol } from '../types/protocol'
import './Protocols.css'

export function Protocols() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [filterStatus, setFilterStatus] = useState<string>('all')

  const { data: protocols, isLoading, error } = useQuery<Protocol[]>({
    queryKey: ['protocols', filterStatus],
    queryFn: () => protocolService.getAll(),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => protocolService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['protocols'] })
    },
  })

  const filteredProtocols = protocols?.filter((p) => filterStatus === 'all' || p.status === filterStatus) || []

  if (isLoading) return <div className="protocols-loading">Loading protocols...</div>
  if (error) return <div className="protocols-error">Error loading protocols</div>

  return (
    <div className="protocols-page">
      <div className="page-header">
        <h1>Healthcare Management Protocols</h1>
        <button className="btn-primary" onClick={() => navigate('/protocols/new')}>
          New Protocol
        </button>
      </div>

      <div className="protocols-filters">
        <button
          className={filterStatus === 'all' ? 'active' : ''}
          onClick={() => setFilterStatus('all')}
        >
          All
        </button>
        <button
          className={filterStatus === 'active' ? 'active' : ''}
          onClick={() => setFilterStatus('active')}
        >
          Active
        </button>
        <button
          className={filterStatus === 'draft' ? 'active' : ''}
          onClick={() => setFilterStatus('draft')}
        >
          Draft
        </button>
        <button
          className={filterStatus === 'inactive' ? 'active' : ''}
          onClick={() => setFilterStatus('inactive')}
        >
          Inactive
        </button>
      </div>

      <div className="protocols-list">
        {filteredProtocols.length === 0 ? (
          <div className="empty-state">
            <p>No protocols found</p>
            <button className="btn-primary" onClick={() => navigate('/protocols/new')}>
              Create First Protocol
            </button>
          </div>
        ) : (
          filteredProtocols.map((protocol) => (
            <div key={protocol.id} className="protocol-card">
              <div className="protocol-header">
                <h3>{protocol.name}</h3>
                <span className={`status-badge status-${protocol.status}`}>{protocol.status}</span>
              </div>
              {protocol.description && <p className="protocol-description">{protocol.description}</p>}
              <div className="protocol-meta">
                {protocol.category && <span className="protocol-category">{protocol.category}</span>}
                {protocol.version && <span className="protocol-version">v{protocol.version}</span>}
              </div>
              <div className="protocol-actions">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/protocols/${protocol.id}`)}
                >
                  View
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/protocols/${protocol.id}/compliance`)}
                >
                  Compliance
                </button>
                <button
                  className="btn-danger"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this protocol?')) {
                      deleteMutation.mutate(protocol.id)
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
